import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { faqData } from '../../data/mock';

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <div 
      className="border-b transition-all duration-300"
      style={{ borderColor: 'rgba(0, 69, 52, 0.1)' }}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span 
          className="heading-3 pr-4 transition-colors duration-300 group-hover:text-[var(--brand-hover)]"
          style={{ color: 'var(--text-primary)', fontSize: 'clamp(1rem, 1.5vw, 1.1rem)' }}
        >
          {question}
        </span>
        <div 
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          style={{ background: isOpen ? 'var(--brand-dark)' : 'rgba(0, 69, 52, 0.1)' }}
        >
          <ChevronDown 
            size={20} 
            style={{ color: isOpen ? 'white' : 'var(--brand-dark)' }} 
          />
        </div>
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="body-medium pr-14" style={{ color: 'var(--text-secondary)' }}>
          {answer}
        </p>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <HelpCircle size={16} />
            <span className="font-semibold text-sm">FAQ</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {faqData.title}
          </h2>
        </div>

        {/* FAQ List */}
        <div 
          className={`max-w-3xl mx-auto card p-6 md:p-8 ${
            isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'
          }`}
          style={{ background: 'white' }}
        >
          {faqData.faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
